import { useGetRunningTotalAiTokenCost } from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { SettingsBlock } from '@repo/common/components/SettingsBlock';
import { ResourceIcon } from '@repo/foundations';

/**
 * RemainingBalanceSettings Component
 *
 * A reusable component for displaying the remaining credit balance.
 * This component shows the current balance and provides context about credit usage.
 *
 * Usage:
 * ```tsx
 * import { RemainingBalanceSettings } from './RemainingBalanceSettings';
 *
 * function MyComponent() {
 *   return <RemainingBalanceSettings />;
 * }
 * ```
 */
export function RemainingBalanceSettings({
	showDescription = true,
}: {
	showDescription?: boolean;
}) {
	const { data: balanceData } = useGetRunningTotalAiTokenCost({});

	const formatCurrency = (amount: number) =>
		new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(amount);

	return (
		<>
			{showDescription && (
				<SettingsButton
					title="Credit balance"
					description="Your credit balance will be consumed with API and AI Chat usage. You can buy credits directly or set up auto-reload thresholds"
					leftComponent={<ResourceIcon iconName="creditCard" />}
				/>
			)}
			<SettingsBlock
				title={`${formatCurrency(balanceData?.balance || 0)} credit balance`}
				description="Credits available for use against API and AI Chat usage"
			/>
		</>
	);
}
